import React from "react"
import { Box, Typography, Stack, Divider, useTheme } from "@carrotfertility/carotene-core"
import { FormattedMessage } from "react-intl"
import { Reimbursement } from "#/types/reimbursement"

const cardTotal = (amount: number, amountAppliedToCostShare?: number | null) => amount + (amountAppliedToCostShare ?? 0)

interface AdjustedAmountRowProps {
  label: React.ReactNode
  originalAmount: number
  amount: number
  formatCurrency: (value: number) => string
}

// A single "label: old (struck through) new" line. Shared by the Carrot Card and Personal
// payment rows so both halves of the adjustment read the same way as the PreAuth estimate
// breakdown rendered elsewhere in TransactionDetailsDrawer (Carrot Card / Personal payment / Total).
const AdjustedAmountRow = ({ label, originalAmount, amount, formatCurrency }: AdjustedAmountRowProps) => (
  <Box display="flex" justifyContent="space-between">
    <Typography variant="body1">{label}</Typography>
    <Typography variant="body1">
      {originalAmount !== amount && (
        <Typography
          component="span"
          variant="body1"
          color="text.secondary"
          sx={(theme) => ({ textDecoration: "line-through", marginInlineEnd: theme.tokens.spacing.xxs })}
        >
          {formatCurrency(originalAmount)}
        </Typography>
      )}
      {formatCurrency(amount)}
    </Typography>
  </Box>
)

interface CardAmountsProps {
  transaction: Reimbursement
  formatCurrency: (value: number) => string
}

// CardExpense equivalent of the PreAuth estimate breakdown (Carrot Card / Personal payment / Total):
// renders when a Carrot Card expense has an adjustment (pay-less via the standard adjustment
// chain, or pay-more via a linked refund — see RefundExpenseService on the backend). Shows the
// Carrot Card / Personal payment split as old (struck through) → new, a Total, and an explanation
// of what the member should expect.
export const CardAmounts = ({ transaction, formatCurrency }: CardAmountsProps) => {
  const theme = useTheme()

  if (!transaction.hasAdjustment) {
    return <Typography variant="body1">{formatCurrency(transaction.amount)}</Typography>
  }

  // adjustmentAmount > 0 → Carrot now covers more of the swipe → the member is reimbursed the difference.
  // adjustmentAmount < 0 → Carrot now covers less → we collect the difference from the member.
  const memberGetsRefund = transaction.adjustmentAmount > 0
  const memberOwesMore = transaction.adjustmentAmount < 0
  const hasPersonalPayment = !!transaction.amountAppliedToCostShare || !!transaction.originalAmountAppliedToCostShare

  return (
    <>
      <Stack spacing={theme.tokens.spacing.xs}>
        <AdjustedAmountRow
          label={<FormattedMessage defaultMessage="Carrot Card" />}
          originalAmount={transaction.originalAmount}
          amount={transaction.amount}
          formatCurrency={formatCurrency}
        />
        {hasPersonalPayment && (
          <AdjustedAmountRow
            label={<FormattedMessage defaultMessage="Personal payment" />}
            originalAmount={transaction.originalAmountAppliedToCostShare ?? 0}
            amount={transaction.amountAppliedToCostShare ?? 0}
            formatCurrency={formatCurrency}
          />
        )}
        <Divider />
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body1" fontWeight="bold">
            <FormattedMessage defaultMessage="Total" />
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {formatCurrency(cardTotal(transaction.amount, transaction.amountAppliedToCostShare))}
          </Typography>
        </Box>
      </Stack>

      {memberGetsRefund && (
        <Typography variant="body1" fontStyle="italic" sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.sm })}>
          <FormattedMessage
            defaultMessage="You'll be reimbursed for your {refundAmount} overpayment. Please check your email for more information."
            values={{ refundAmount: formatCurrency(transaction.adjustmentAmount) }}
          />
        </Typography>
      )}
      {memberOwesMore && (
        <Typography variant="body1" fontStyle="italic" sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.sm })}>
          <FormattedMessage
            defaultMessage="We'll collect an additional {additionalAmount} from you. Please check your email for more information."
            values={{ additionalAmount: formatCurrency(Math.abs(transaction.adjustmentAmount)) }}
          />
        </Typography>
      )}
    </>
  )
}
